import { Helmet } from "react-helmet";
import EstimateForm from "@/components/EstimateForm";
import EstimateFAQ from "@/components/EstimateFAQ";
import EstimateProcess from "@/components/EstimateProcess";
import MiniTestimonials from "@/components/MiniTestimonials";
import { CheckCircle, Video, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const EstimatePage = () => {
  return (
    <>
      <Helmet>
        <title>Get Free Estimate - Professional Cleaning Services in Connecticut | Routine Cleaners</title>
        <meta 
          name="description" 
          content="Get your free cleaning estimate in Connecticut. Fill out our detailed form or schedule a video call for a personalized quote. Fast, accurate, and no obligation." 
        />
        <meta name="keywords" content="free estimate, cleaning quote, Connecticut cleaning, professional cleaning estimate" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-clean pt-20 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
              Get Your <span className="bg-gradient-to-r from-brand-gold-dark to-brand-gold-light bg-clip-text text-transparent">Free Estimate</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              The more details you provide, the faster we can give you an accurate estimate. 
              Choose your preferred way to connect with us.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Mobile: Form first, then sidebar */}
            <div className="block lg:hidden space-y-8">
              <EstimateForm />
              
              {/* Mobile Contact Options */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <Video className="h-5 w-5 mr-2 text-accent" />
                      Video Call (Recommended)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Book your video call directly! We'll call you at your chosen time to walk through your space together and collect all the information we need.
                    </p>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-accent mr-2 flex-shrink-0" />
                        Most accurate pricing
                      </div>
                      <div className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-accent mr-2 flex-shrink-0" />
                        Instant questions & answers
                      </div>
                      <div className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-accent mr-2 flex-shrink-0" />
                        15-20 minute call
                      </div>
                    </div>
                    <Button className="w-full" asChild>
                      <a href="https://api.syncfunnel.com/widget/bookings/videocallcalendar" target="_blank" rel="noopener noreferrer">
                        Schedule Video Call Now
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <Phone className="h-5 w-5 mr-2 text-accent" />
                      Phone Consultation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Schedule a convenient time for us to call you. No more annoying calls - we'll respect your schedule and call only at your preferred time.
                    </p>
                    <div className="space-y-4">
                      <Button variant="outline-blue" className="w-full" asChild>
                        <a href="https://api.syncfunnel.com/widget/bookings/freecleaningconsultation" target="_blank" rel="noopener noreferrer">
                          Schedule Phone Consultation
                        </a>
                      </Button>
                      <Button variant="outline-gold" className="w-full" asChild>
                        <a href="tel:+14752517156">Call Now: (475) 251-7156</a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Trust Indicators */}
                <div className="bg-muted/50 p-6 rounded-lg">
                  <h3 className="font-semibold text-foreground mb-3">Why Choose Routine Cleaners?</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-accent mr-2 flex-shrink-0" />
                      Licensed & Insured
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-accent mr-2 flex-shrink-0" />
                      100% Satisfaction Guarantee
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-accent mr-2 flex-shrink-0" />
                      No Contracts Required
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-accent mr-2 flex-shrink-0" />
                      Same-Day Response
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop: Sidebar first, then form */}
            <div className="hidden lg:grid lg:grid-cols-3 lg:col-span-3 gap-8">
              {/* Contact Options */}
              <div className="lg:col-span-1 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <Video className="h-5 w-5 mr-2 text-accent" />
                      Video Call (Recommended)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Book your video call directly! We'll call you at your chosen time to walk through your space together and collect all the information we need.
                    </p>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-accent mr-2 flex-shrink-0" />
                        Most accurate pricing
                      </div>
                      <div className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-accent mr-2 flex-shrink-0" />
                        Instant questions & answers
                      </div>
                      <div className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-accent mr-2 flex-shrink-0" />
                        15-20 minute call
                      </div>
                    </div>
                    <Button className="w-full mb-2" asChild>
                      <a href="https://api.syncfunnel.com/widget/bookings/videocallcalendar" target="_blank" rel="noopener noreferrer">
                        Schedule Video Call Now
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <Phone className="h-5 w-5 mr-2 text-accent" />
                      Phone Consultation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Schedule a convenient time for us to call you. No more annoying calls - we'll respect your schedule and call only at your preferred time.
                    </p>
                    <div className="space-y-2">
                      <Button variant="outline-blue" className="w-full" asChild>
                        <a href="https://api.syncfunnel.com/widget/bookings/freecleaningconsultation" target="_blank" rel="noopener noreferrer">
                          Schedule Phone Consultation
                        </a>
                      </Button>
                      <Button variant="outline-gold" className="w-full" asChild>
                        <a href="tel:+14752517156">Call Now: (475) 251-7156</a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Trust Indicators */}
                <div className="bg-muted/50 p-6 rounded-lg">
                  <h3 className="font-semibold text-foreground mb-3">Why Choose Routine Cleaners?</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-accent mr-2 flex-shrink-0" />
                      Licensed & Insured
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-accent mr-2 flex-shrink-0" />
                      100% Satisfaction Guarantee
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-accent mr-2 flex-shrink-0" />
                      No Contracts Required
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-accent mr-2 flex-shrink-0" />
                      Same-Day Response
                    </div>
                  </div>
                </div>
              </div>

              {/* Estimate Form */}
              <div className="lg:col-span-2">
                <EstimateForm />
              </div>
            </div>
          </div>
        </div>
        
        {/* New sections */}
        <EstimateFAQ />
        <EstimateProcess />
        <MiniTestimonials />
      </div>
    </>
  );
};

export default EstimatePage;